// Sparkline — pure SVG (Sprint 7 Push 2B).
//
// Bewusst KEIN Recharts: Hero-Tiles brauchen 4-6 Sparklines auf Mobile, Recharts
// instanziert pro Chart einen ResponsiveContainer + ResizeObserver. Pure-SVG ist
// 1-2 KB Render-Cost statt ~30 KB pro Mount. Spec ruleset 8.1: kein Overengineering.
//
// Severity-Farben gemappt aus lib/severity.ts (cyan/amber/pink). Slate fuer
// no_data / placeholder. Path wird auf 0..100 normiert und via SVG-viewBox
// auf Tile-Breite skaliert (responsive).

import { useId, useMemo } from "react";

import type { Severity } from "../../lib/severity";

export type SparklineColor = Severity | "slate";

interface Props {
  /** Numerische Reihe. Mindestens 2 Punkte sonst rendert die Komponente nichts. */
  data: number[];
  /** Severity-Farbe oder slate fuer Placeholder. Default cyan (ok). */
  color?: SparklineColor;
  /** Expliziter Hex-Wert (z.B. Firmen-Markenfarbe) — ueberschreibt `color`.
   *  Auftrag H Schritt 3: Firmen-Sparklines einfarbig im Firmen-Hex. */
  hex?: string;
  /** Pixel-Hoehe — Breite ist immer 100% des Containers. Default 32. */
  height?: number;
  /** Markiert den letzten Datenpunkt mit einem Dot. */
  showLast?: boolean;
  /** Aria-Label — Hero-Tiles brauchen das fuer Screenreader-Kontext. */
  ariaLabel?: string;
  /** Optionale Extra-Klasse fuer Outer-SVG (z.B. opacity-60 als Hintergrund). */
  className?: string;
}

const COLOR_MAP: Record<SparklineColor, { stroke: string; fill: string; dot: string }> = {
  ok: { stroke: "#22d3ee", fill: "#22d3ee", dot: "#67e8f9" },
  warn: { stroke: "#fbbf24", fill: "#fbbf24", dot: "#fcd34d" },
  critical: { stroke: "#ec4899", fill: "#ec4899", dot: "#f9a8d4" },
  slate: { stroke: "#64748b", fill: "#64748b", dot: "#94a3b8" },
};

const VIEW_W = 100;
const VIEW_H = 32;
const PAD_Y = 2;

export default function Sparkline({
  data,
  color = "ok",
  hex,
  height = 32,
  showLast = false,
  ariaLabel,
  className,
}: Props) {
  const gradId = useId();

  const geom = useMemo(() => {
    if (data.length < 2) return null;
    let min = Infinity;
    let max = -Infinity;
    for (const v of data) {
      if (v < min) min = v;
      if (v > max) max = v;
    }
    // Flat-Line absichern — 0-Range wuerde div/0 produzieren.
    const range = max - min;
    const norm = (v: number): number =>
      range === 0
        ? VIEW_H / 2
        : VIEW_H - PAD_Y - ((v - min) / range) * (VIEW_H - 2 * PAD_Y);
    const stepX = VIEW_W / (data.length - 1);
    const points = data.map((v, i) => `${(i * stepX).toFixed(2)},${norm(v).toFixed(2)}`);
    const linePath = `M ${points.join(" L ")}`;
    const areaPath = `${linePath} L ${VIEW_W},${VIEW_H} L 0,${VIEW_H} Z`;
    const lastX = (data.length - 1) * stepX;
    const lastY = norm(data[data.length - 1]);
    return { linePath, areaPath, lastX, lastY };
  }, [data]);

  if (!geom) return null;
  const c = hex != null ? { stroke: hex, fill: hex, dot: hex } : COLOR_MAP[color];

  return (
    <svg
      role={ariaLabel ? "img" : "presentation"}
      aria-label={ariaLabel}
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      className={className}
    >
      <defs>
        <linearGradient id={gradId} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={c.fill} stopOpacity={0.35} />
          <stop offset="100%" stopColor={c.fill} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={geom.areaPath} fill={`url(#${gradId})`} />
      <path
        d={geom.linePath}
        fill="none"
        stroke={c.stroke}
        strokeWidth={1.4}
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      {showLast && (
        <circle
          cx={geom.lastX}
          cy={geom.lastY}
          r={1.8}
          fill={c.dot}
          stroke={c.stroke}
          strokeWidth={0.5}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}
